<template>
  <div class="flex flex-col w-full h-full">
    <!-- Üst Bilgi ve Filtreler -->
    <div class="sticky-header flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3 px-1 py-2 w-full sticky top-0 bg-white z-10 border-b border-gray-200">
      <div class="flex items-center gap-2">
        <h3 class="text-lg font-medium text-gray-700 whitespace-nowrap">Tüm Belgeler</h3>
        <span v-if="selectedDocuments.length > 0" class="bg-primary text-white text-xs rounded-full px-2 py-1">
          {{ selectedDocuments.length }} belge seçildi
        </span>
      </div>

      <!-- Kontroller -->
      <div class="flex flex-wrap items-center gap-2 w-full md:w-auto">
        <!-- Toplu İşlem Butonları -->
        <div v-if="selectedDocuments.length > 0" class="flex items-center gap-2 w-full md:w-auto">
          <button @click="showBulkDeleteConfirm = true" class="flex items-center space-x-1 px-3 py-1.5 rounded-md bg-red-50 border border-red-300 text-red-600 hover:bg-red-100 transition-colors" :disabled="isDeleting">
            <span class="material-symbols-outlined !text-[18px]">delete</span> <span class="text-sm font-medium">Seçilenleri Sil</span>
          </button>
          <button @click="clearSelection" class="flex items-center space-x-1 px-3 py-1.5 rounded-md bg-white border border-gray-300 hover:bg-gray-50 transition-colors">
            <span class="material-symbols-outlined !text-[18px]">clear</span> <span class="text-sm font-medium">Seçimi Temizle</span>
          </button>
        </div>

        <!-- Desktop Controls: Arama, Filtrele, Yenile -->
        <div v-else class="w-full md:w-auto flex flex-wrap items-center gap-2">
          <!-- Search Input Removed - Now only in sidebar -->

          <!-- Mobile Controls: Filtrele Button (Full Width) and Refresh -->
          <div class="flex md:hidden w-full gap-2">
            <button @click="toggleFilters" class="flex-grow flex items-center justify-center px-3 py-2 rounded-md bg-white border border-gray-300 hover:bg-gray-50 transition-colors" :class="{'text-primary border-primary/40 bg-primary/5': hasActiveFilters}">
              <span class="material-symbols-outlined !text-[18px] mr-1">filter_alt</span> <span class="text-sm font-medium">Filtrele</span>
              <span v-if="hasActiveFilters" class="bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs ml-1">{{ activeFilterCount }}</span>
            </button>

            <button @click="refreshDocuments" class="flex items-center justify-center w-12 h-10 rounded-md bg-white border border-gray-300 text-primary hover:bg-primary/5 transition-colors" :disabled="isLoading" title="Listeyi yenile">
              <span class="material-symbols-outlined !text-[18px]" :class="{'animate-spin': isLoading}">refresh</span>
            </button>
          </div>

          <!-- Desktop Only: Filter and Refresh buttons for DOCUMENTS TAB ONLY -->
          <div class="hidden md:flex items-center gap-2">
            <!-- Yenile Butonu -->
            <button @click="refreshDocuments" class="flex items-center justify-center w-9 h-9 rounded-md bg-white border border-gray-300 text-primary hover:bg-primary/5 transition-colors" :disabled="isLoading" title="Listeyi yenile">
              <span class="material-symbols-outlined !text-[18px]" :class="{'animate-spin': isLoading}">refresh</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Aktif Filtre Etiketleri -->
    <div v-if="hasActiveFilters" class="flex flex-wrap gap-2 mb-4 px-1">
      <div v-for="(filter, key) in activeFilters" :key="key" class="flex items-center bg-primary/10 text-primary text-xs rounded-full px-3 py-1">
        <span>{{ filter.label }}: {{ filter.value }}</span>
        <button @click="removeFilter(key)" class="ml-1.5 hover:text-primary-dark">
          <span class="material-symbols-outlined !text-[14px]">close</span>
        </button>
      </div>
    </div>

    <!-- Belgeler Listesi - Using VTable -->
    <div class="table-container flex-1 min-h-[300px] max-h-[calc(100vh-350px)] flex flex-col overflow-hidden bg-white rounded-lg border border-gray-200">
      <VTable :data="documents" :columns="tableColumns" :isLoading="isLoading && !isInitialLoading" :selectable="true" :selectedIds="selectedDocuments" :showSelectAll="true" :showPagination="false" :minWidth="'100%'" :maxHeight="'100%'" :minHeight="'300px'" :noDataText="getEmptyStateMessage()" :sortKey="sortColumn" :sortOrder="sortDirection" @update:selectedIds="handleSelectionChange" @row-click="handleRowClick" @sort="handleSortChange" class="thin-scrollbar">
        <!-- Document Name Column -->
        <template #fileName="{ item }">
          <div class="flex items-start gap-3">
            <!-- Dosya ikonu - ortalanmış -->
            <div class="flex items-center justify-center w-6">
              <span class="material-symbols-outlined text-gray-400" v-if="isImageDocument(item)">image</span>
              <span class="material-symbols-outlined text-gray-400" v-else-if="isPresentationDocument(item)">slideshow</span>
              <span class="material-symbols-outlined text-gray-400" v-else>description</span>
            </div>

            <!-- Belge adı ve açıklama - sağda -->
            <div class="truncate flex-1 flex flex-col">
              <!-- Belge adı direkt gösterimi -->
              <p class="font-medium text-gray-900 truncate">
                {{ truncateText(item.fileName, 30) }}
              </p>

              <!-- Description direkt belge adının altında -->
              <div v-if="item.description" class="text-xs text-gray-500 truncate mt-1 flex items-center">
                <span class="material-symbols-outlined !text-[14px] mr-1">info</span>
                <span>{{ truncateText(item.description, 30) }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Add Date Column -->
        <template #addDate="{ item }">
          <div class="flex items-center justify-center h-full relative z-20">
            <span class="text-sm text-gray-600">
              {{ formatDate(item.addDate) }}
            </span>
          </div>
        </template>

        <!-- Categories Column -->
        <template #categories="{ item }">
          <div class="flex flex-wrap justify-center items-center gap-1 h-full relative z-20">
            <template v-if="item.categories && item.categories.length > 0">
              <span v-for="(category, index) in item.categories.slice(0, 2)" :key="index" class="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full whitespace-nowrap">
                {{ category.name }}
              </span>
              <span v-if="item.categories.length > 2" class="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full cursor-pointer">
                +{{ item.categories.length - 2 }}
              </span>
            </template>
            <span v-else class="text-gray-400 text-xs">-</span>
          </div>
        </template>

        <!-- File Type Column -->
        <template #fileType="{ item }">
          <div class="flex items-center justify-center h-full relative z-20">
            <span :class="{
                'bg-blue-100 text-blue-800': item.fileType === 'DOSYA',
                'bg-purple-100 text-purple-800': item.fileType === 'SUNUM',
                'bg-green-100 text-green-800': item.fileType === 'GÖRSEL',
                'bg-red-100 text-red-800': item.fileType === 'VİDEO',
                'bg-yellow-100 text-yellow-800': item.fileType === 'TEKLİF',
                'bg-indigo-100 text-indigo-800': item.fileType === 'PROSEDÜR',
                'bg-pink-100 text-pink-800': item.fileType === 'TALİMAT',
                'bg-gray-100 text-gray-600': !item.fileType
              }" class="px-2 py-1 text-xs font-medium rounded-full inline-block">
              {{ item.fileType || '-' }}
            </span>
          </div>
        </template>

        <!-- State Column -->
        <template #state="{ item }">
          <div class="flex items-center justify-center h-full relative z-20">
            <span :class="getStateColor(item.state)" class="px-2 py-1 text-xs font-medium rounded-full inline-flex items-center justify-center mx-auto">
              <span v-if="getStateIcon(item.state)" class="material-symbols-outlined !text-[14px] mr-1">
                {{ getStateIcon(item.state) }}
              </span>
              {{ getStateText(item.state) }}
            </span>
          </div>
        </template>

        <!-- Actions Column -->
        <template #actions="{ item }">
          <div class="flex items-center justify-center h-full relative z-20">
            <div class="flex items-center justify-center space-x-1">
              <!-- Edit Categories Button -->
              <button @click.stop="editCategories(item)" class="text-primary hover:text-primary-dark flex items-center relative p-1 rounded-full hover:bg-primary/5" title="Kategorileri Düzenle" aria-label="Kategorileri düzenle">
                <span class="material-symbols-outlined !text-[20px]">category</span>
              </button>

              <!-- Delete Button -->
              <button @click.stop="deleteDocument(item.id)" class="text-red-500 hover:text-red-700 flex items-center relative p-1 rounded-full hover:bg-red-50" :disabled="item.isDeleting" title="Belgeyi Sil" aria-label="Belgeyi sil">
                <span v-if="item.isDeleting" class="absolute inset-0 flex items-center justify-center">
                  <span class="animate-spin h-4 w-4 border-2 border-red-500 rounded-full border-t-transparent"></span>
                </span>
                <span :class="{'opacity-0': item.isDeleting}" class="material-symbols-outlined !text-[20px]">delete</span>
              </button>

              <!-- View Button -->
              <a :href="item.data" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-700 flex items-center p-1 rounded-full hover:bg-blue-50" title="Belgeyi Görüntüle" @click.stop>
                <span class="material-symbols-outlined !text-[20px]">open_in_new</span>
              </a>
            </div>
          </div>
        </template>
      </VTable>
    </div>

    <!-- Pagination with page numbers -->
    <div class="pagination-container mt-auto py-3 px-4 border-t border-gray-200 flex items-center justify-between flex-wrap">
      <!-- Total results moved to center on mobile -->
      <div class="flex items-center justify-center text-sm text-gray-500 w-full mb-2 sm:mb-0 sm:w-auto sm:justify-start">
        <span>{{ props.pagination?.total || 0 }} sonuç</span>
      </div>

      <div class="flex items-center justify-center gap-2 w-full sm:w-auto sm:justify-end">
        <!-- Page Navigation Buttons - Compact on Mobile -->
        <div class="flex items-center rounded-md overflow-hidden border border-gray-200">
          <!-- First page button (hidden on small mobile) -->
          <button @click="changePage(1)" :disabled="currentPage === 1" class="navigation-btn" :class="{'hidden': isExtraSmall}" title="İlk sayfa">
            <span class="material-symbols-outlined !text-[18px]">keyboard_double_arrow_left</span>
          </button>

          <!-- Previous button -->
          <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="navigation-btn border-l border-r border-gray-200" title="Önceki sayfa">
            <span class="material-symbols-outlined !text-[18px]">chevron_left</span>
          </button>

          <!-- Current/Total Pages Display -->
          <div class="px-2 sm:px-4 h-8 flex items-center justify-center">
            <span class="text-xs sm:text-sm text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
          </div>

          <!-- Next button -->
          <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="navigation-btn border-l border-r border-gray-200" title="Sonraki sayfa">
            <span class="material-symbols-outlined !text-[18px]">chevron_right</span>
          </button>

          <!-- Last page button (hidden on small mobile) -->
          <button @click="changePage(totalPages)" :disabled="currentPage === totalPages" class="navigation-btn" :class="{'hidden': isExtraSmall}" title="Son sayfa">
            <span class="material-symbols-outlined !text-[18px]">keyboard_double_arrow_right</span>
          </button>
        </div>

        <!-- Page Size Selector - Using VSelect for all devices -->
        <div class="hidden sm:block">
          <div class="flex items-center">
            <span class="text-sm text-gray-700 mr-2 whitespace-nowrap">Sayfa Başına:</span>
            <VSelect :options="pageSizeOptions" :modelValue="props.pagination?.pageSize || 10" @update:modelValue="changePageSize" size="sm" :clearable="false" :showSearch="false" class="w-24"/>
          </div>
        </div>

        <!-- Mobile-only more compact version -->
        <VSelect v-if="isMobile" :options="pageSizeOptions" :modelValue="props.pagination?.pageSize || 10" @update:modelValue="changePageSize" size="sm" :clearable="false" :placeholder="'Sayfa Başına'" :showSearch="false" class="sm:hidden w-20 min-w-[5rem]"/>
      </div>
    </div>

    <!-- Toplu Silme Onay Diyalogu -->
    <DeleteConfirmDialog v-model:show="showBulkDeleteConfirm" :ids="selectedDocuments" :is-processing="isDeleting" @confirm="confirmBulkDelete"/>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, reactive, onMounted, computed, onUnmounted, nextTick, shallowRef } from 'vue'
import CustomSelect from '@/components/base/inputs/VSelect.vue'
import VInput from '@/components/base/inputs/VInput.vue'
import VSelect from '@/components/base/inputs/VSelect.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'
import VTooltip from '@/components/common/VTooltip.vue'
import DeleteConfirmDialog from './DeleteConfirmDialog.vue'
import VTable from '@/components/table/VTable.vue'
import pagination from '@/components/base/pagination/index.vue'

import {
  truncateText,
  isTextTruncated,
  getCategoryList,
  getStateText,
  getStateColor,
  getStateIcon,
  formatDate,
} from '../utils'

const props = defineProps({
  documents: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isInitialLoading: {
    type: Boolean,
    default: false,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object,
    default: () => ({
      currentPage: 1,
      pageSize: 10,
      total: 0,
    }),
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
  searchQuery: {
    type: String,
    default: '',
  },
  showFilters: {
    type: Boolean,
    default: false,
  },
  categoryOptions: {
    type: Array,
    default: () => [],
  },
  fileTypeOptions: {
    type: Array,
    default: () => [],
  },
  dateFilterOptions: {
    type: Array,
    default: () => [],
  },
  activeFilters: {
    type: Object,
    default: () => ({}),
  },
  hasActiveFilters: {
    type: Boolean,
    default: false,
  },
  activeFilterCount: {
    type: Number,
    default: 0,
  },
  selectedDocumentIds: {
    type: Array,
    default: () => [],
  },
  // Sıralama durumu için yeni prop'lar
  sortHistory: {
    type: Object,
    default: () => ({
      lastColumn: 'addDate',
      columnStates: {
        addDate: 'DESC',
        fileName: null,
        fileType: null,
        state: null,
      },
    }),
  },
})

const emit = defineEmits([
  'refresh',
  'delete',
  'bulk-delete',
  'edit-categories',
  'apply-filters',
  'reset-filters',
  'remove-filter',
  'search',
  'clear-search',
  'update-pagination',
  'toggle-filters',
  'update:search-query',
  'selection-cleared',
  'select-document',
  'sort-change',
])

// Screen size detection
const windowWidth = ref(window.innerWidth)
const isMobile = computed(() => windowWidth.value < 640)
const isExtraSmall = computed(() => windowWidth.value < 480)

// Sıralama durumu - varsayılan olarak yükleme tarihine göre azalan sıralama
const sortColumn = ref('addDate')
const sortDirection = ref('DESC')

// Lifecycle hook - Bileşen oluşturulduğunda, varsayılan sıralamayı ayarla 
onMounted(() => {
  // Direkt varsayılan sıralamayı ayarla
  // Sayfa ilk açıldığında tarih sütunu için OK işareti gösterilsin
  sortColumn.value = 'addDate'
  sortDirection.value = 'DESC'

  // Varsayılan sıralama durumunu ayarla
  // Bu atamalar artık güvenli çünkü bunlar computed değil ref değişkenler
  nextTick(() => {
    emit('sort-change', {
      field: 'addDate',
      direction: 'desc',
    })
  })
})

// Window resize listener
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

// Add resize event listener
onMounted(() => {
  window.addEventListener('resize', updateWindowWidth)
})

// Remove event listeners when component is unmounted
onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth)
})

// Toplu seçim yönetimi - Büyük listeler için shallowRef kullanarak performans artışı
const selectedDocuments = shallowRef([...(props.selectedDocumentIds || [])])
const showBulkDeleteConfirm = ref(false)

// Sayfa seçici
const showPageSelector = ref(false)

// Handle selection change from VTable
const handleSelectionChange = (ids) => {
  if (Array.isArray(ids)) {
    // Seçili belgeleri yerel olarak güncelle
    selectedDocuments.value = [...ids]

    // Ana belge bileşenine seçimleri bildir
    emit('select-document', [...ids])
  }
}

// Calculate if all items are selected
const isAllSelected = computed(() => {
  if (!props.documents || props.documents.length === 0) return false
  return props.documents.length > 0 &&
      selectedDocuments.value.length === props.documents.length
})

// Calculate if some but not all items are selected
const isIndeterminate = computed(() => {
  return selectedDocuments.value.length > 0 &&
      selectedDocuments.value.length < (props.documents?.length || 0)
})

// Boş durum mesajını oluştur
const getEmptyStateMessage = () => {
  if (props.hasActiveFilters) {
    return 'Belirtilen filtrelere uygun belge bulunamadı. Lütfen filtrelerinizi değiştirin veya yeni belgeler yükleyin.'
  }
  return 'Sistemde kayıtlı belge bulunamadı. Dosya yükleme ekranından yeni belgeler ekleyebilirsiniz.'
}


// Table columns configuration
const tableColumns = [
  {
    key: 'fileName',
    label: 'Belge Adı',
    width: '30%',
    align: 'left',
    sortable: true,
  },
  {
    key: 'addDate',
    label: 'Yüklenme Tarihi',
    width: '15%',
    align: 'center',
    sortable: true, // Default olarak bu sütun sıralanıyor
  },
  {
    key: 'categories',
    label: 'Kategori',
    width: '15%',
    align: 'center',
    // Kategoriler array olduğu için sıralama mantıklı değil
  },
  {
    key: 'fileType',
    label: 'Dosya Türü',
    width: '15%',
    align: 'center',
    sortable: true,
  },
  {
    key: 'state',
    label: 'Durum',
    width: '15%',
    align: 'center',
    sortable: true,
  },
  {
    key: 'actions',
    label: 'İşlemler',
    width: '80px',
    align: 'center',
    // İşlem sütunu sıralanmamalı
  },
]

// Check if a specific document is selected
const isDocumentSelected = (documentId) => {
  return selectedDocuments.value.includes(documentId)
}

// Toggle selection of a document
const toggleDocumentSelection = (documentId) => {
  const newSelection = [...selectedDocuments.value]

  if (isDocumentSelected(documentId)) {
    const index = newSelection.indexOf(documentId)
    if (index !== -1) {
      newSelection.splice(index, 1)
    }
  } else {
    newSelection.push(documentId)
  }

  selectedDocuments.value = newSelection
  emit('select-document', newSelection)
}

// Toggle all documents selection
const toggleAllSelection = () => {
  let newSelection = []

  if (!isAllSelected.value) {
    // Tümünü seç
    newSelection = props.documents.map(doc => doc.id)
  }

  // Seçimleri güncelle
  selectedDocuments.value = newSelection
  emit('select-document', newSelection)
}

// Synchronize with external selectedDocumentIds
watch(() => props.selectedDocumentIds, (newIds) => {
  if (newIds && Array.isArray(newIds)) {
    // Değerleri karşılaştır, sadece değişirse güncelle
    const currentIds = selectedDocuments.value
    const newIdsStr = JSON.stringify(newIds.slice().sort())
    const currentIdsStr = JSON.stringify(currentIds.slice().sort())

    if (newIdsStr !== currentIdsStr) {
      selectedDocuments.value = [...newIds]
    }
  }
}, { deep: false })

// Seçimi temizle
const clearSelection = () => {
  selectedDocuments.value = []
  emit('select-document', [])
  emit('selection-cleared')
}

// Handle row click event
const handleRowClick = (item) => {
  // Could implement custom row click behavior here
}

// NOT: VTable'ın hangi sıralama durumunda olduğunu artık state.sortHistory içinde takip ediyoruz
// Bu durumda bu değişkenleri aşağıdaki gibi güncelliyoruz
// ve bunları yerel duruma bağlıyoruz:

// Son sıralanan sütun ve durum - yerel ref olarak tutuyoruz (computed değil)
const lastSortedColumn = ref('addDate')
const sortCycle = ref({
  addDate: 'DESC', // varsayılan
  fileName: null,
  fileType: null,
  state: null,
})

// Props değişince güncellemek için watch
watch(() => props.sortHistory, (newHistory) => {
  if (newHistory) {
    lastSortedColumn.value = newHistory.lastColumn || 'addDate'
    sortCycle.value = newHistory.columnStates || {
      addDate: 'DESC',
      fileName: null,
      fileType: null,
      state: null,
    }
  }
}, { immediate: true, deep: true })

const handleSortChange = (sortOptions) => {
  if (sortOptions) {
    let field, direction

    if (sortOptions.key) {
      field = sortOptions.key
      direction = sortOptions.order
    } else {
      if (lastSortedColumn.value === 'addDate') {
        field = 'addDate'
        direction = 'ASC'
      } else {
        field = 'addDate'
        direction = 'DESC'
      }
    }

    sortColumn.value = field
    sortDirection.value = direction

    emit('sort-change', {
      field: field,
      direction: direction.toLowerCase(),
    })
  }
}

// Toplu silme işlemini onayla
const confirmBulkDelete = () => {
  // Seçili belgelerin geçerli bir kopyasını al
  const idsToDelete = [...selectedDocuments.value]

  // Silme işlem diyaloğunu kapat
  showBulkDeleteConfirm.value = false

  // Seçimi temizle (silme işlemi başlamadan önce)
  clearSelection()

  // Silme işlemini başlat
  if (idsToDelete.length > 0) {
    emit('bulk-delete', idsToDelete)
  }
}

// Yerel filtreler - prop'ları doğrudan değiştirmemek için
const localFilters = reactive({
  fileType: props.filters?.fileType || null,
  categoryId: props.filters?.categoryId || null,
  dateFilter: props.filters?.dateFilter || null,
})

// Arama metni yerel durumu
const localSearchQuery = ref(props.searchQuery || '')

// searchQuery prop'una reaktif güncelleme
watch(() => props.searchQuery, (newQuery) => {
  if (newQuery !== localSearchQuery.value) {
    localSearchQuery.value = newQuery || ''
  }
})

// Arama metni güncelleme fonksiyonu
const updateSearchQuery = (value) => {
  if (typeof value === 'string') {
    localSearchQuery.value = value
    emit('update:search-query', value)
  }
}

// Pagination variables and functions
const currentPage = computed(() => props.pagination?.currentPage || 1)
const totalPages = computed(() => {
  const total = props.pagination?.total || 0
  const pageSize = props.pagination?.pageSize || 10
  return Math.max(1, Math.ceil(total / pageSize))
})

// Calculate visible page numbers for pagination
const visiblePageNumbers = computed(() => {
  const maxVisiblePages = isMobile.value ? 3 : 5
  const result = []

  if (totalPages.value <= maxVisiblePages) {
    // Show all pages if there are few pages
    for (let i = 1; i <= totalPages.value; i++) {
      result.push(i)
    }
  } else {
    // Always show first page
    result.push(1)

    // Calculate start and end of visible pages around current page
    let startPage = Math.max(2, currentPage.value - 1)
    let endPage = Math.min(totalPages.value - 1, currentPage.value + 1)

    // Show ellipsis if there's a gap after page 1
    if (startPage > 2) {
      result.push('...')
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      result.push(i)
    }

    // Show ellipsis if there's a gap before last page
    if (endPage < totalPages.value - 1) {
      result.push('...')
    }

    // Always show last page if not already included
    if (totalPages.value > 1) {
      result.push(totalPages.value)
    }
  }

  return result
})

// Page size options for VSelect
const pageSizeOptions = computed(() => {
  return [10, 25, 50, 100].map(size => ({
    label: String(size),
    value: size,
  }))
})

// Sayfa seçici değişkenleri (pagination için eski değişkenler)
const currentPageInput = ref(currentPage.value)
const selectedPageSize = ref(props.pagination?.pageSize || 10)

// Page selector functions
const closePageSelector = () => {
  showPageSelector.value = false
}

// Change page
const changePage = (page) => {
  if (page < 1 || page > totalPages.value || page === currentPage.value) {
    return
  }

  emit('update-pagination', {
    currentPage: page,
    pageSize: props.pagination?.pageSize || 10,
    total: props.pagination?.total || 0,
  })
}

// Change page size
const changePageSize = (size) => {
  // Reset to page 1 when changing page size
  const newPage = 1

  emit('update-pagination', {
    currentPage: newPage,
    pageSize: size,
    total: props.pagination?.total || 0,
  })
}

// Update currentPageInput when currentPage changes
watch(currentPage, (newPage) => {
  if (currentPageInput) {
    currentPageInput.value = newPage
  }
})

// Update selectedPageSize when pagination.pageSize changes
watch(() => props.pagination?.pageSize, (newSize) => {
  if (newSize && selectedPageSize) {
    selectedPageSize.value = newSize
  }
})

// Dosya türü yardımcı metodları
const isImageDocument = (document) => {
  return document && document.fileType === 'GÖRSEL'
}

const isPresentationDocument = (document) => {
  return document && document.fileType === 'SUNUM'
}

// Event handler metotları
const refreshDocuments = () => {
  emit('refresh')
}

const deleteDocument = (documentId) => {
  emit('delete', documentId)
}

const editCategories = (document) => {
  emit('edit-categories', document)
}

const resetFilters = () => {
  // Yerel filtreleri sıfırla
  localFilters.fileType = null
  localFilters.categoryId = null
  localFilters.dateFilter = null
  emit('reset-filters')
}

const removeFilter = (filterKey) => {
  emit('remove-filter', filterKey)
}

const searchDocuments = () => {
  emit('search')
}

const clearSearch = () => {
  localSearchQuery.value = ''
  emit('clear-search')
}

const toggleFilters = () => {
  emit('toggle-filters')
}

// Props değiştiğinde yerel filtreler güncellenir
onMounted(() => {
  // İlk yüklemede yerel filtreler ayarlanır
  if (props.filters) {
    localFilters.fileType = props.filters.fileType || null
    localFilters.categoryId = props.filters.categoryId || null
    localFilters.dateFilter = props.filters.dateFilter || null
  }

  // Sayfa için değişkenlerimizi ayarla
  if (currentPageInput && currentPage.value) {
    currentPageInput.value = currentPage.value
  }

  if (selectedPageSize && props.pagination?.pageSize) {
    selectedPageSize.value = props.pagination?.pageSize || 10
  }
})
</script>

<style scoped>
/* Pagination styling */
.navigation-btn {
  @apply w-8 h-8 flex items-center justify-center;
  @apply transition-colors bg-white;
  @apply text-gray-700 hover:bg-gray-100;
}

.navigation-btn:disabled {
  @apply text-gray-300 bg-gray-50 cursor-not-allowed hover:bg-gray-50;
}

/* Custom dropdown positioning - make it appear above the component */
:deep(.pagination-container .floating-dropdown) {
  top: auto !important;
  bottom: 40px !important;
  z-index: 9999 !important;
}

/* Responsive design for mobile */
@media (max-width: 640px) {
  .pagination-container {
    @apply flex-wrap items-center justify-center py-2;
  }

  .pagination-container > div:first-child {
    @apply mb-2 w-full text-center;
  }

  .pagination-container > div:last-child {
    @apply justify-center;
  }

  .navigation-btn {
    @apply w-9;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .pagination-container span {
    @apply text-xs;
  }

  .navigation-btn {
    @apply w-8;
  }
}
</style>